<template>
  <form ref="formConversionesTipoNota" @submit.prevent="guardar">
    <InputCsrf />
    <div class="row">
      <div class="col-sm-8">
        <input-base
          ref="inputNombre"
          v-model="conversion.nombre"
          :error="errores.nombre"
          :label-key="$I18n.t('activerecord.attributes.calificaciones/conversion.nombre')"
          :required="true"
        />
      </div>
      <div class="col-sm-2 m-t-lg">
        <input-checkbox-boolean v-model="conversion.activa">
          {{ $I18n.t('activerecord.attributes.calificaciones/conversion.activa') }}
        </input-checkbox-boolean>
      </div>
      <div class="col-sm-2 m-t-lg">
        <input-checkbox-boolean v-model="conversion.conversion_defecto">
          {{ $I18n.t('activerecord.attributes.calificaciones/conversion.conversion_defecto') }}
        </input-checkbox-boolean>
      </div>
    </div>
    <div class="form-group row">
      <div class="col-sm-12">
        <input-base
          ref="inputDescripcion"
          v-model="conversion.descripcion"
          :error="errores.descripcion"
          :label-key="$I18n.t('activerecord.attributes.calificaciones/conversion.descripcion')"
          :required="false"
        />
      </div>
    </div>
    <div class="form-group row">
      <div class="col-sm-4">
        <label for="" class="input-label">
          <abbr title="requerido">*</abbr>
          {{ $I18n.t('activerecord.attributes.calificaciones/conversion.tipo_conversion') }}
        </label>
        <select v-model="conversion.tipo_conversion" class="form-control">
          <option
            v-for="option in tiposConversiones"
            :key="option.value"
            :value="option.value"
            :disabled="isTipoConversionDisabled(option.value)"
          >
            {{ option.name }}
          </option>
        </select>
        <p v-if="errores.tipo_conversion" class="text-danger">
          {{ modelErrorsToHtmlText(errores.tipo_conversion) }}
        </p>
      </div>
      <div class="col-sm-4">
        <label for="" class="input-label">
          <abbr title="requerido">*</abbr>
          {{ $I18n.t('activerecord.attributes.calificaciones/conversion.tipo_nota_resultado') }}
        </label>
        <select v-model="conversion.tipo_nota_resultado_id" class="form-control">
          <option
            v-for="option in tiposNotas"
            :key="option.id"
            :value="option.id"
            :disabled="isTipoNotaResultadoDisabled(option)"
          >
            {{ option.nombre }}
          </option>
        </select>
        <p v-if="errores.tipo_nota_resultado" class="text-danger">
          {{ modelErrorsToHtmlText(errores.tipo_nota_resultado) }}
        </p>
      </div>
      <div class="col-sm-4 m-t-lg">
        <input-checkbox-boolean v-model="conversion.mostrar_nota_original">
          {{ $I18n.t('activerecord.attributes.calificaciones/conversion.mostrar_nota_original') }}
        </input-checkbox-boolean>
      </div>
    </div>
    <transition enter-active-class="animated fadeIn">
      <div v-if="isTipoConversionManual()">
        <valores-form
          :model-value="valores"
          :error="errores['conversion_valores.valor_convertido']"
          @update:model-value="conversion.conversion_valores=$event"
        >
        </valores-form>
      </div>
    </transition>
    <div class="form-actions m-t-lg">
      <a v-if="cancelarUrl" class="btn btn-default" :href="cancelarUrl">
        <i class="fa fa-angle-left"></i> {{ $I18n.t('Cancelar') }}
      </a>
      <boton-guardar :submitting="guardando"></boton-guardar>
    </div>
  </form>
</template>

<script>
import { cloneDeep } from 'lodash';
import axios from 'axios';
import calificaciones from '@/api/calificaciones';
import { getModelJsonErrors, modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';
import ValoresForm from './ValoresForm.vue';
import { redirectTo } from '../../../../libs/cifra/urls';

export default {
  components: { ValoresForm },
  props: {
    model: { type: Object, required: true },
    cancelarUrl: { type: String, default: null },
  },
  data() {
    return {
      guardando: false,
      conversion: ({ ...cloneDeep(this.model) }) || {
        nombre: null,
        descripcion: null,
        activa: false,
        conversion_valores: [],
      },
      errores: {},
      tiposConversiones: [
        { name: this.$I18n.t('activerecord.enums.calificaciones/conversion.tipo_conversion.manual'), value: 'manual' },
        { name: this.$I18n.t('activerecord.enums.calificaciones/conversion.tipo_conversion.directa'), value: 'directa' },
      ],
      tiposNotas: [],
    };
  },
  computed: {
    valores() {
      return this.model.conversion_valores || [];
    },
  },
  mounted() {
    this.$refs.inputNombre.focus();
    calificaciones.maestros.getTiposNotas().then((result) => {
      this.tiposNotas = result.data;
    });
  },
  methods: {
    isTipoNotaNumerica() {
      return this.conversion.tipo_nota.tipo_nota === 'numerica';
    },
    isTipoNotaTexto() {
      return this.conversion.tipo_nota.tipo_nota === 'texto';
    },
    isTipoConversionManual() {
      return this.conversion.tipo_conversion === this.tiposConversiones[0].value;
    },
    isTipoConversionDirecta() {
      return this.conversion.tipo_conversion === this.tiposConversiones[1].value;
    },
    isTipoConversionDisabled(tipo) {
      return !this.isTipoNotaNumerica() && tipo === this.tiposConversiones[1].value;
    },
    isTipoNotaResultadoDisabled(tipoNota) {
      return (this.isTipoConversionDirecta() && tipoNota.tipo_nota === 'texto')
        || tipoNota.id === this.conversion.tipo_nota.id;
    },
    guardar() {
      this.guardando = true;

      const urlBase = `/calificaciones/tipo_notas/${this.conversion.tipo_nota.id}/conversiones`;
      let url = urlBase;
      let method = 'post';
      if (this.conversion.id) {
        url = `${url}/${this.conversion.id}`;
        method = 'patch';
      }

      this.conversion.conversion_valores_attributes = this.conversion.conversion_valores;

      axios({
        url,
        method,
        data: { calificaciones_tipo_nota_conversion: this.conversion },
      }).then(() => {
        this.errores = {};
        redirectTo(urlBase);
      }).catch((e) => {
        this.errores = getModelJsonErrors(e.response.data);
      }).then(() => {
        this.guardando = false;
      });
    },
    modelErrorsToHtmlText,
  },
};
</script>

<style scoped>
  table > tbody > tr > td:last-child {
    text-align: center;
  }
  abbr {
    border: 0;
    text-decoration: none;
    color: #fe1d12;
  }
</style>
